/**
 * NetworkDrawer — "Add network" / "Edit network" modal (May 2026 Figma).
 *
 *   ┌─────────── Add network ───────────┐
 *   │ Network name (SSID)*  [SSID     ] │
 *   │ Security type*        [WPA     ▾] │
 *   │ Password*             [••••   👁] │
 *   │ Site*                 [Select ▾] │
 *   │                                   │
 *   │              Cancel  [ Add ]      │
 *   └───────────────────────────────────┘
 *
 * Single-pane modal (no left rail) — reuses the shared `.os-modal-single`
 * shell as TeamModal / DockDrawer.
 */
import { useEffect, useState } from 'react';
import deviceService from '../../services/deviceService';

const SECURITY_TYPES = ['WPA', 'WPA2', 'WPA3', 'WPA2-Enterprise', 'WEP', 'Open'];

const EMPTY = {
  ssid: '',
  securityType: 'WPA',
  password: '',
  siteId: '',
  status: 'OFFLINE',
};

function FieldLabel({ children, required }) {
  return (
    <label className="os-label">
      <span>{children}{required && <span className="os-req">*</span>}</span>
    </label>
  );
}

export default function NetworkDrawer({
  open,
  orgId,
  initial = null,           // existing wifi network DTO (edit mode)
  sites = [],
  onClose,
  onSaved,                  // (network) => void  — called after successful create/update
}) {
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!initial;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        ssid: initial.ssid ?? initial.networkName ?? initial.name ?? '',
        securityType: initial.securityType ?? initial.securityProtocol ?? initial.security ?? initial.authType ?? 'WPA',
        password: initial.password ?? '',
        siteId: String(initial.siteId ?? initial.site?.siteId ?? initial.site?.id ?? ''),
        status: (initial.status ?? (initial.isActive === false ? 'OFFLINE' : 'OFFLINE')).toString().toUpperCase(),
      });
    } else {
      setForm(EMPTY);
    }
    setShowPassword(false);
    setShowErrors(false);
    setError(null);
  }, [open, initial]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, saving]);

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const ssidErr     = showErrors && !form.ssid?.trim();
  const passwordErr = showErrors && !form.password;
  const siteErr     = showErrors && !form.siteId;

  const idOf   = (s) => s.siteId ?? s.id;
  const nameOf = (s) => s.siteName ?? s.name ?? `Site ${idOf(s)}`;

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!form.ssid?.trim()) return;
    if (!form.password) return;
    if (!form.siteId) return;

    const payload = {
      orgId: Number(orgId) || orgId,
      siteId: Number(form.siteId) || form.siteId,
      ssid: form.ssid.trim(),
      securityType: form.securityType,
      password: form.password,
    };
    // Only send status from the edit screen — create defaults to OFFLINE on the server.
    if (isEdit) payload.status = form.status;

    setSaving(true);
    setError(null);
    try {
      const saved = isEdit
        ? await deviceService.updateWifiNetwork(initial.networkId ?? initial.wifiNetworkId ?? initial.id, payload, orgId)
        : await deviceService.createWifiNetwork(payload, orgId);
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message ?? e.message ?? 'Failed to save network');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit network' : 'Add network'}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="os-modal os-modal-single nw-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>{isEdit ? 'Edit network' : 'Add network'}</h2>
        </header>

        <div className="os-modal-body nw-body">
          <section className="os-pane">
            {/* Network name (SSID) */}
            <div className="os-field nw-row">
              <FieldLabel required>Network name (SSID)</FieldLabel>
              <div className="nw-control">
                <input
                  type="text"
                  className={`os-input ${ssidErr ? 'os-input-error' : ''}`}
                  placeholder="SSID"
                  value={form.ssid}
                  onChange={(e) => setField('ssid', e.target.value)}
                  autoFocus
                />
                {ssidErr && (
                  <div className="os-field-error">
                    <i className="fas fa-info-circle"></i> This field is required
                  </div>
                )}
              </div>
            </div>

            {/* Security type */}
            <div className="os-field nw-row">
              <FieldLabel required>Security type</FieldLabel>
              <div className="nw-control">
                <select
                  className="os-input os-select"
                  value={form.securityType}
                  onChange={(e) => setField('securityType', e.target.value)}
                >
                  {SECURITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Password */}
            <div className="os-field nw-row">
              <FieldLabel required>Password</FieldLabel>
              <div className="nw-control">
                <div className="nw-password">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`os-input ${passwordErr ? 'os-input-error' : ''}`}
                    placeholder="Password"
                    value={form.password}
                    onChange={(e) => setField('password', e.target.value)}
                  />
                  <button
                    type="button"
                    className="nw-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                {passwordErr && (
                  <div className="os-field-error">
                    <i className="fas fa-info-circle"></i> This field is required
                  </div>
                )}
              </div>
            </div>

            {/* Site */}
            <div className="os-field nw-row">
              <FieldLabel required>Site</FieldLabel>
              <div className="nw-control">
                <select
                  className={`os-input os-select ${siteErr ? 'os-input-error' : ''} ${!form.siteId ? 'is-placeholder' : ''}`}
                  value={form.siteId}
                  onChange={(e) => setField('siteId', e.target.value)}
                >
                  <option value="" disabled>Select a site</option>
                  {sites.map((s) => (
                    <option key={idOf(s)} value={String(idOf(s))}>{nameOf(s)}</option>
                  ))}
                </select>
                {siteErr && (
                  <div className="os-field-error">
                    <i className="fas fa-info-circle"></i> Please select a site
                  </div>
                )}
              </div>
            </div>

            {/* Status (edit only — create defaults to Offline on the server) */}
            {isEdit && (
              <div className="os-field nw-row">
                <FieldLabel>Status</FieldLabel>
                <div className="nw-control">
                  <select
                    className="os-input os-select"
                    value={form.status}
                    onChange={(e) => setField('status', e.target.value)}
                  >
                    <option value="OFFLINE">Offline</option>
                    <option value="ONLINE">Online</option>
                  </select>
                </div>
              </div>
            )}

            {error && (
              <div className="os-field-error" style={{ marginTop: 6 }}>
                <i className="fas fa-exclamation-triangle"></i> {error}
              </div>
            )}
          </section>
        </div>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? (isEdit ? 'Saving…' : 'Adding…') : (isEdit ? 'Save' : 'Add')}
          </button>
        </footer>
      </div>
    </div>
  );
}

